import type { UseFormSetValue } from 'react-hook-form';

import type { ServiceFormData } from '@/lib/validators/service';
import { RECURRING_BOOKING_MODE } from '@/types/recurring-booking-mode';

/**
 * A primeira pergunta do assistente: como o cliente compra.
 *
 * São duas respostas possíveis, não três. Existia uma "assinatura com horário
 * fixo" e uma "assinatura por pacote", e a separação não se sustentava: privar
 * o assinante de marcar sempre no mesmo horário nunca fez sentido, e o pacote
 * sem horário fixo não passava de um avulso que pode marcar N vezes. Sobrou a
 * assinatura simples — paga todo mês, marca até X vezes no período — e marcar
 * sempre na mesma terça virou conveniência na hora de montar a agenda.
 *
 * No formulário isso são dois campos (`isSubscription`,
 * `recurringBookingMode`) que só fazem sentido combinados.
 */
export const SALE_MODE = {
  /** Assinatura: paga por ciclo, marca dentro da cota do período. */
  SUBSCRIPTION: 'SUBSCRIPTION',
  /** Sessão avulsa: marca uma data por vez, sem contrato. */
  SINGLE: 'SINGLE',
} as const;

export type SaleMode = (typeof SALE_MODE)[keyof typeof SALE_MODE];

export interface SaleModeOption {
  value: SaleMode;
  title: string;
  description: string;
  example: string;
}

export const SALE_MODE_OPTIONS: SaleModeOption[] = [
  {
    value: SALE_MODE.SUBSCRIPTION,
    title: 'Assinatura',
    description:
      'O cliente paga todo mês e pode marcar um número de sessões por período, quando quiser.',
    example: 'R$ 400/mês · 4 sessões',
  },
  {
    value: SALE_MODE.SINGLE,
    title: 'Sessão avulsa',
    description:
      'O cliente marca uma data por vez e paga aquela sessão. Sem contrato.',
    example: 'R$ 80 · por sessão',
  },
];

export function saleModeOf(values: { isSubscription?: boolean }): SaleMode {
  return values.isSubscription ? SALE_MODE.SUBSCRIPTION : SALE_MODE.SINGLE;
}

/**
 * Escrever a escolha no formulário. Assinatura é sempre recorrente e sempre
 * agendável — sem duração ela não vira horário nenhum, e o passo da agenda
 * cobra esse número em vez de arbitrar um.
 */
export function applySaleMode(setValue: UseFormSetValue<ServiceFormData>, mode: SaleMode) {
  const options = { shouldDirty: true } as const;

  if (mode === SALE_MODE.SINGLE) {
    setValue('isSubscription', false, options);
    setValue('recurringBookingMode', RECURRING_BOOKING_MODE.OFF, options);
    return;
  }

  setValue('isSubscription', true, options);
  setValue('isSchedulable', true, options);
  setValue('recurringBookingMode', RECURRING_BOOKING_MODE.AUTO, options);
}
